/**
 * electric claimable claim <claimId>
 *
 * Claim flow:
 * 1. Check claimable source is ready
 * 2. If it has a claim_link (database not yet claimed), show it and exit
 * 3. If claim_link is null (database already claimed), call the claim API
 *    to move the source into the user's workspace
 */

import { Command } from 'commander'
import {
  createClient,
  createPublicClient,
  getDashboardUrl,
} from '../../client.js'
import { handleError, exitWithError, EXIT_CONFLICT } from '../../lib/errors.js'
import { resolveWorkspace } from '../../lib/workspace-resolver.js'
import {
  isQuiet,
  printQuiet,
  printJson,
  printSuccess,
  printKeyValue,
} from '../../output.js'

export const claimCommand = new Command(`claim`)
  .description(`Claim a provisioned service into your workspace`)
  .argument(`<claimId>`, `Claim ID`)
  .option(`--workspace <id>`, `Workspace ID`)
  .option(
    `--environment <id>`,
    `Environment ID (optional — auto-created if omitted)`
  )
  .action(async (claimId: string, opts) => {
    const json = claimCommand.parent?.parent?.opts().json ?? false
    try {
      await runClaim(claimId, opts, json)
    } catch (error) {
      await handleError(error, json)
    }
  })

async function runClaim(
  claimId: string,
  opts: { workspace?: string; environment?: string },
  json: boolean
): Promise<void> {
  const publicClient = createPublicClient()

  // 1. Validate the claimable source is ready
  const status = await publicClient.claimable.get({ claimId })

  if (status.state === `claimed`) {
    exitWithError(`This service has already been claimed.`, EXIT_CONFLICT, json)
  }

  if (status.state !== `ready`) {
    exitWithError(
      `Claimable service is not ready (state: ${status.state}).`,
      EXIT_CONFLICT,
      json
    )
  }

  // 2. If there's a claim_link, the database hasn't been claimed yet.
  //    Show the dashboard claim URL and exit.
  if (status.claim_link) {
    const claimUrl = `${getDashboardUrl()}/claim?uuid=${claimId}`
    if (json) {
      printJson({ claimId, claim_url: claimUrl, claimed: false })
    } else {
      process.stderr.write(`Visit the link below to claim the database:\n\n`)
      process.stdout.write(`${claimUrl}\n`)
    }
    return
  }

  // 3. Database already claimed — move the source into the user's workspace
  if (!json) {
    process.stderr.write(`Database claimed. Adding service to workspace...\n`)
  }

  const client = createClient()
  const workspaceId = await resolveWorkspace(client, opts.workspace)

  const result = await client.claimable.claim({
    claimId,
    workspaceId,
    ...(opts.environment ? { environmentId: opts.environment } : {}),
  })

  if (isQuiet()) {
    printQuiet(result.serviceId)
    return
  }

  if (json) {
    printJson(result)
  } else {
    printSuccess(`Source claimed successfully.\n`)
    printKeyValue([
      [`Service ID:`, result.serviceId],
      [`Service type:`, result.serviceType],
      [`Project ID:`, result.projectId],
      [`Environment ID:`, result.environmentId],
    ])
  }
}
